'use client';

import { useFiltersContext } from '@components/FilterPage/Context/useFiltersContext';
import { Flex, freddyTheme, Switch, Typography } from '@coolblue-development/freddy';
import selectFilterTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentFilter/selectFilterTrigger';
import { SmileHappy } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useTranslation } from '@i18n/useTranslation';
import { useEffect, useRef } from 'react';

type Props = {
  totalVacancies: number;
};

export const InternalUserFacet = ({ totalVacancies }: Props) => {
  const { t } = useTranslation('filterPage.filterFacets');

  const { getSelectedFilterOptions, setFilter, isPending } = useFiltersContext();
  const { trackEvent } = useAnalytics();

  const isInternalUserSelected = getSelectedFilterOptions('visibility').includes('internal');

  const pendingFilterValue = useRef<string | null>(null);

  useEffect(() => {
    // isPending helps to send totalVacancies after loading them and
    // pendingFilterValue helps to trackEvent only after clicking the toggle
    if (isPending || pendingFilterValue.current === null) return;

    trackEvent(
      selectFilterTrigger({
        sub_feature: 'internal_toggle',
        filter_name: 'internal user',
        filter_value: pendingFilterValue.current,
        number_of_items: totalVacancies,
        is_recommended: false
      })
    );
    pendingFilterValue.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending]);

  const handleToggle = () => {
    setFilter('visibility', isInternalUserSelected ? [] : ['internal']);
    pendingFilterValue.current = isInternalUserSelected ? 'false' : 'true';
  };

  return (
    <Flex alignItems='center' gap={freddyTheme.spacing[200]}>
      <Switch
        checked={isInternalUserSelected}
        onChange={handleToggle}
        sx={{
          backgroundColor: isInternalUserSelected
            ? freddyTheme.color.background.interactiveActionBase
            : undefined
        }}
        aria-label='Internal vacancies switcher'
      />
      <Flex alignItems='center' gap={freddyTheme.spacing[100]}>
        <SmileHappy size='large' fill={freddyTheme.color.font.headingMain} />
        <Typography
          fontWeight={freddyTheme.typography.fontWeight.dobraSlab.medium}
          fontFamily='DobraSlabMedium'
          color={freddyTheme.color.font.headingMain}
        >
          {t('visibility.label')}
        </Typography>
      </Flex>
    </Flex>
  );
};
